/* global showInfoToast */
import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import Calendar from "./tools/Calendar";
import FooterBar from "./layout/FooterBar";
import FooterButton from "./tools/FooterButton";
import { getUser, getUsers, getCarriers, getTargetTypes, getTargetItems, getTargets } from "../api/api";
import { AuthContext } from './context/AuthContext';
import { SearchContext } from './layout/SearchContext';
import globals from '../config/globals.json';
import groupTargetItemsByContact from '../utils/groupTargetItemsByContact';
import OrderItemModal from './elements/OrderItemModal';
import './elements/CardWithLogo.css';

const STATUS_ICONS = {
    0: { icon: "hourglass_empty", label: "INSERITO", color: "text-secondary" },
    1: { icon: "check_circle", label: "ATTIVO", color: "text-success" },
    2: { icon: "warning", label: "RICHIEDE ATTENZIONE", color: "text-yellow" },
    3: { icon: "cancel", label: "ANNULLATO", color: "text-warning" },
    4: { icon: "build", label: "KO TECNICO", color: "text-danger" },
    5: { icon: "gavel", label: "KO AMMINISTRATIVO", color: "text-danger" }
};

const getDefaultDateRange = () => {
    const now = new Date();
    const firstOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastOfCurrentMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const formatDate = date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

    return {
        // start: formatDate(firstOfLastMonth),
        start: "",
        end: formatDate(lastOfCurrentMonth)
    };
};

const OrderTracking = (props) => {
    const companyID = props.userDetails?.Company?.[0]?.dataID || null;
    const { user } = useContext(AuthContext);
    const { searchTerm, clearSearch } = useContext(SearchContext);
    const [userDetails, setUserDetails] = useState(null);
    const [isEditable, setIsEditable] = useState(false);
    const [, setIsDeletable] = useState(false);
    const [targetItems, setTargetItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadProgress, setLoadProgress] = useState(null);
    const [loadError, setLoadError] = useState("");
    const [carrierFilter, setCarrierFilter] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    const [userFilter, setUserFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [callFilter, setCallFilter] = useState("");
    const [sortOrder, setSortOrder] = useState("date-desc");
    const [dateStart, setDateStart] = useState(() => getDefaultDateRange().start);
    const [dateEnd, setDateEnd] = useState(() => getDefaultDateRange().end);
    const [targetTypes, setTargetTypes] = useState([]);
    const [carriers, setCarriers] = useState([]);
    const [users, setUsers] = useState([]);
    const [showOnlyFilteredData, setShowOnlyFilteredData] = useState(false);
    const [selectedTargetItem, setSelectedTargetItem] = useState(null);
    const fetchRequestId = useRef(0);
    const [availableTargets, setAvailableTargets] = useState([]);

    const handleQualitySaved = (updatedTarget) => {
        setSelectedTargetItem(updatedTarget);
        setTargetItems(groups => groups.map(group => group.map(item =>
            item.node.id === updatedTarget.id ? { ...item, node: { ...item.node, ...updatedTarget } } : item
        )));
    };

    const handleStatusSaved = (updatedTarget) => {
        setSelectedTargetItem(updatedTarget);
        setTargetItems(groups => groups.map(group => group.map(item =>
            item.node.id === updatedTarget.id ? { ...item, node: { ...item.node, ...updatedTarget } } : item
        )));
    };

    const loadTargets = async () => {
        const response = await getTargets(companyID, "", "", selectedTargetItem?.date?.slice(0, 7));
        setAvailableTargets(response.data.data.getTargetListing.edges);
    };

    const loadAllTargetItems = useCallback(async () => {
        const requestId = ++fetchRequestId.current;
        const pageSize = 500;
        setLoading(true);
        setLoadProgress(null);
        setLoadError("");

        try {
            const firstResponse = await getTargetItems(companyID, pageSize, 0, "");
            const firstPageItems = firstResponse.data?.data?.getTargetItemListing?.edges;
            if (!Array.isArray(firstPageItems)) {
                throw new Error("Risposta non valida durante il caricamento della timeline");
            }
            if (requestId !== fetchRequestId.current) return;

            let loadedItems = firstPageItems;
            setTargetItems(groupTargetItemsByContact(firstPageItems));
            setLoading(false);

            let page = 1;
            let hasMore = firstPageItems.length === pageSize;
            const totalCount = firstResponse.data?.data?.getTargetItemListing?.totalCount || 0;
            const updateProgress = (loaded) => setLoadProgress(
                totalCount ? Math.min(99, Math.round((loaded / totalCount) * 100)) : 0
            );
            if (hasMore) updateProgress(loadedItems.length);

            while (hasMore) {
                const response = await getTargetItems(companyID, pageSize, page * pageSize, "");
                const pageItems = response.data?.data?.getTargetItemListing?.edges;
                if (!Array.isArray(pageItems)) {
                    throw new Error("Risposta non valida durante il caricamento della timeline");
                }
                if (requestId !== fetchRequestId.current) return;

                loadedItems = loadedItems.concat(pageItems);
                updateProgress(loadedItems.length);
                setTargetItems(currentItems => groupTargetItemsByContact(pageItems, currentItems));
                page++;
                hasMore = pageItems.length === pageSize;
            }
        } catch (error) {
            console.error("Errore nel caricamento della timeline:", error);
            if (requestId === fetchRequestId.current) {
                setLoadError("Non è stato possibile caricare tutti gli elementi. Riprova.");
            }
        } finally {
            if (requestId === fetchRequestId.current) {
                setLoading(false);
                setLoadProgress(null);
            }
        }
    }, [companyID]);

    useEffect(() => {
        if (!companyID) return undefined;

        let cancelled = false;
        const loadTimelineOptions = async () => {
            try {
                const [userResponse, usersResponse, carriersResponse, typesResponse] = await Promise.all([
                    getUser(user),
                    getUsers(companyID),
                    getCarriers(companyID),
                    getTargetTypes(companyID)
                ]);
                if (cancelled) return;

                setUserDetails(userResponse.data?.data?.getUserListing?.edges?.[0]?.node || null);
                setUsers(usersResponse.data?.data?.getUserListing?.edges || []);
                setCarriers(carriersResponse.data?.data?.getCarrierListing?.edges || []);
                setTargetTypes(typesResponse.data?.data?.getTargetTypeListing?.edges || []);
            } catch (error) {
                console.error("Errore nel caricamento dei filtri della timeline:", error);
                if (!cancelled) {
                    setLoadError("Non è stato possibile caricare i dati della timeline. Riprova.");
                }
            }
        };

        loadTimelineOptions();
        return () => {
            cancelled = true;
        };
    }, [companyID, user]);

    useEffect(() => {
        if (companyID) {
            loadAllTargetItems();
        }
        return () => {
            fetchRequestId.current += 1;
        };
    }, [companyID, loadAllTargetItems]);

    useEffect(() => {
        loadTargets();
    }, [selectedTargetItem]);

    const getTargetDetails = (item) => item?.node || {};
    // Ogni parola cercata deve comparire da qualche parte (nome, cognome, CF, operatore), in qualsiasi ordine
    const matchesSearch = (contact, targetName) => {
        const tokens = searchTerm.trim().toLowerCase().split(/\s+/).filter(Boolean);
        if (tokens.length === 0) return true;
        const text = [contact.lastName, contact.firstName, contact.fiscalCode, targetName]
            .filter(Boolean).join(" ").toLowerCase();
        return tokens.every(token => text.includes(token));
    };
    const matchesStatusAndCall = (item) => {
        const target = getTargetDetails(item);
        if (statusFilter !== "" && String(Number(target.status) || 0) !== statusFilter) return false;
        const hasCheckCall = Number(target.check) === 1;
        const hasQualityCall = Number(target.evaluation) > 0;
        if (callFilter === "check" && !hasCheckCall) return false;
        if (callFilter === "quality" && !hasQualityCall) return false;
        if (callFilter === "check-none" && hasCheckCall) return false;
        if (callFilter === "quality-none" && hasQualityCall) return false;
        return true;
    };
    const matchesSelectedFilters = (item) => {
        const target = getTargetDetails(item);
        if (!matchesStatusAndCall(item)) return false;
        const contact = target.Contact?.[0] || {};
        const targetName = target.Target?.[0]?.name || "";
        const targetType = target.Target?.[0]?.TargetType?.[0]?.name || "";
        const username = target.User?.[0]?.username || "";

        if (carrierFilter && !targetName.startsWith(carrierFilter)) return false;
        if (userFilter && !username.startsWith(userFilter)) return false;
        if (typeFilter && !targetType.startsWith(typeFilter)) return false;

        const date = target.date ? target.date.slice(0, 10) : "";
        if (dateStart && date < dateStart) return false;
        if (dateEnd && date > dateEnd) return false;

        if (searchTerm && !matchesSearch(contact, targetName)) return false;

        return true;
    };

    const filterGroups = (group) => {
        if (showOnlyFilteredData) return group.some(matchesSelectedFilters);

        if (carrierFilter && !group.some(item =>
            (getTargetDetails(item).Target?.[0]?.name || "").startsWith(carrierFilter)
        )) return false;
        if (userFilter && !group.some(item =>
            (getTargetDetails(item).User?.[0]?.username || "").startsWith(userFilter)
        )) return false;
        if (typeFilter && !group.some(item =>
            (getTargetDetails(item).Target?.[0]?.TargetType?.[0]?.name || "").startsWith(typeFilter)
        )) return false;
        if ((statusFilter !== "" || callFilter) && !group.some(matchesStatusAndCall)) return false;
        if ((dateStart || dateEnd) && !group.some(item => {
            const date = getTargetDetails(item).date?.slice(0, 10) || "";
            return (!dateStart || date >= dateStart) && (!dateEnd || date <= dateEnd);
        })) return false;
        if (searchTerm) {
            const contact = getTargetDetails(group[0]).Contact?.[0] || {};
            if (!group.some(item => matchesSearch(contact, getTargetDetails(item).Target?.[0]?.name))) return false;
        }
        return true;
    };

    // Con il toggle attivo l'ordinamento si basa sugli elementi visibili, non su quelli nascosti
    const visibleGroups = targetItems
        .filter(filterGroups)
        .map(group => showOnlyFilteredData ? group.filter(matchesSelectedFilters) : group)
        .sort((a, b) => {
            const targetA = getTargetDetails(a[0]);
            const targetB = getTargetDetails(b[0]);
            if (sortOrder === "date-asc" || sortOrder === "date-desc") {
                const dateA = targetA.date ? new Date(targetA.date) : new Date(0);
                const dateB = targetB.date ? new Date(targetB.date) : new Date(0);
                return sortOrder === "date-asc" ? dateA - dateB : dateB - dateA;
            }

            const contactA = targetA.Contact?.[0] || {};
            const contactB = targetB.Contact?.[0] || {};
            const nameA = `${contactA.lastName || ""} ${contactA.firstName || ""}`.toUpperCase();
            const nameB = `${contactB.lastName || ""} ${contactB.firstName || ""}`.toUpperCase();
            return sortOrder === "asc"
                ? nameA.localeCompare(nameB, "it", { sensitivity: "base" })
                : nameB.localeCompare(nameA, "it", { sensitivity: "base" });
        });

    return (
        <div className="container-fluid">
            <div className="row clearfix justify-content-center mb-10">
                <div className="col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-center mb-2 row">
                                <div className="form-group align-items-center mb-2 col-xl col-md-3 col-6">
                                    <select className="form-control" value={carrierFilter} onChange={e => setCarrierFilter(e.target.value)}>
                                        <option value="">Tutti gli operatori</option>
                                        {carriers.map(carrier => (
                                            <option key={carrier.node.id} value={carrier.node.code}>{carrier.node.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group align-items-center mb-2 col-xl col-md-3 col-6">
                                    <select className="form-control" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
                                        <option value="">Tutti i tipi</option>
                                        {targetTypes.map((type, index) => (
                                            <option key={type.node.id || index} value={type.node.name}>{type.node.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group align-items-center mb-2 col-xl col-md-3 col-6">
                                    <select className="form-control" value={userFilter} onChange={e => setUserFilter(e.target.value)}>
                                        {userDetails && userDetails.role <= 4 ? (
                                            <>
                                                <option value="">Tutti gli utenti</option>
                                                {users.map(userItem => (
                                                    <option key={userItem.node.username} value={userItem.node.username}>{userItem.node.username}</option>
                                                ))}
                                            </>
                                        ) : (
                                            <option value={user?.toUpperCase() || ""}>{user?.toUpperCase()}</option>
                                        )}
                                    </select>
                                </div>
                                <div className="form-group align-items-center mb-2 col-xl col-md-3 col-6">
                                    <select className="form-control" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                                        <option value="">Tutti gli stati</option>
                                        {Object.entries(STATUS_ICONS).map(([statusId, status]) => (
                                            <option key={statusId} value={statusId}>{status.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group align-items-center mb-2 col-xl col-md-3 col-6">
                                    <select className="form-control" value={callFilter} onChange={e => setCallFilter(e.target.value)}>
                                        <option value="">Check/Quality call</option>
                                        <option value="check">Con check call</option>
                                        <option value="quality">Con quality call</option>
                                        <option value="check-none">Senza check call</option>
                                        <option value="quality-none">Senza quality call</option>
                                    </select>
                                </div>
                                <div className="form-group align-items-center mb-2 col-xl col-md-3 col-6">
                                    <select className="form-control" value={sortOrder} onChange={e => setSortOrder(e.target.value)}>
                                        <option value="asc">Ordine A-Z</option>
                                        <option value="desc">Ordine Z-A</option>
                                        <option value="date-desc">Più recenti</option>
                                        <option value="date-asc">Più vecchi</option>
                                    </select>
                                </div>
                                <div className="btn-group align-items-center mb-2 col-xl col-md-3 col-12">
                                    <button
                                        type="button"
                                        className="btn btn-secondary py-1"
                                        title="Reset filtri"
                                        onClick={() => {
                                            const defaultDateRange = getDefaultDateRange();
                                            clearSearch();
                                            setDateStart(defaultDateRange.start);
                                            setDateEnd(defaultDateRange.end);
                                            setTypeFilter("");
                                            setCarrierFilter("");
                                            setUserFilter("");
                                            setStatusFilter("");
                                            setCallFilter("");
                                            setSortOrder("date-desc");
                                        }}
                                    >
                                        <span className="material-symbols-outlined" style={{ fontSize: "20px" }}>replay</span>
                                    </button>
                                    <button type="button" className="btn btn-primary ml-2 py-1" title="Ricarica timeline" onClick={loadAllTargetItems}>
                                        <span className="material-symbols-outlined" style={{ fontSize: "20px" }}>refresh</span>
                                    </button>
                                </div>
                            </div>
                            <style>{`.order-tracking-links { --bm-accent: #2563EB; --bm-tint: rgba(127,127,127,0.08); --bm-border: rgba(127,127,127,0.22); margin-bottom: 16px; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 8px; }
.order-tracking-links .bm-item--link { flex-direction: row; align-items: center; gap: 8px; padding: 8px 10px; border-radius: 12px; }
.order-tracking-links .bm-item--link .bm-media { width: 32px; height: 32px; border-radius: 8px; }
.order-tracking-links .bm-item--link .bm-media .material-symbols-outlined { font-size: 18px; }
.order-tracking-links .bm-item--link .bm-name { font-size: 12px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.order-tracking-links .bm-item--link .bm-sub { margin-top: 0; font-size: 10px; }
.order-tracking-links .bm-item--link .ik { font-size: 10px; }
.order-tracking-links .bm-item--link .bm-text > .d-flex { margin-top: 2px !important; }
.order-tracking-links .bm-item--link .bm-text { width: auto; }
.order-tracking-links .bm-arrow { position: static; font-size: 18px; }
.ot-contact { display: flex; align-items: center; gap: 12px; margin: 8px 0 10px; }
.ot-contact__avatar { display: flex; flex: none; align-items: center; justify-content: center; width: 40px; height: 40px; border-radius: 50%; background: rgba(37,99,235,0.14); color: #2563EB; font-size: 14px; font-weight: 700; letter-spacing: 0.03em; }
.ot-contact__text { display: flex; flex-direction: column; min-width: 0; line-height: 1.25; }
.ot-contact__name { font-size: 15px; font-weight: 700; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ot-contact__sub { font-size: 11px; font-weight: 600; letter-spacing: 0.04em; opacity: 0.6; }
.ot-contact__count { margin-left: auto; padding: 1px 8px; border-radius: 999px; background: rgba(127,127,127,0.12); font-size: 11px; font-weight: 600; }
.ot-filter-footer { display: flex; align-items: center; margin-right: 12px; cursor: pointer; }
.ot-filter-footer .account-filter-toggle { display: flex; align-items: center; margin: 0; cursor: pointer; }
.ot-filter-track::after { display: none; }
.ot-filter-knob { position: absolute; top: 2px; left: 2px; display: flex; align-items: center; justify-content: center; width: 22px; height: 22px; border-radius: 50%; background: #fff; color: #6c757d; font-size: 14px; box-shadow: 0 1px 3px rgba(0,0,0,0.25); transition: transform 180ms ease, color 180ms ease; }
.account-filter-toggle__input:checked + .ot-filter-track .ot-filter-knob { transform: translateX(18px); color: #34c759; }
@media (max-width: 575.98px) {
.order-tracking-links { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.order-tracking-links .bm-item--link { padding: 6px 8px; gap: 6px; }
.order-tracking-links .bm-item--link .bm-media { width: 26px; height: 26px; }
.order-tracking-links .bm-item--link .bm-name { font-size: 11px; }
.order-tracking-links .bm-item--link .bm-sub { font-size: 9px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
}`}</style>
                            <div className="row small text-muted mb-3 mx-0 justify-content-sm-center" aria-label="Legenda stati">
                                {[0, 1, 2, 3, 4, 5].map(statusId => {
                                    const status = STATUS_ICONS[statusId];
                                    return (
                                        <span key={status.label} className="col-6 col-sm-auto d-flex align-items-center px-1 px-sm-2 mb-1">
                                            <span className={`material-symbols-outlined ${status.color} mr-1`} aria-hidden="true" style={{ fontSize: "18px" }}>
                                                {status.icon}
                                            </span>
                                            <span>{status.label}</span>
                                        </span>
                                    );
                                })}
                            </div>
                            <div className="profiletimeline mt-0">
                                {loadError && <div className="alert alert-warning" role="alert">{loadError}</div>}
                                {loading && (
                                    <div className="text-center my-4">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="sr-only">Loading...</span>
                                        </div>
                                    </div>
                                )}
                                {visibleGroups.map((group, groupIndex) => {
                                    const contact = getTargetDetails(group[0]).Contact?.[0] || {};
                                    return (
                                        <React.Fragment key={contact.id || groupIndex}>
                                            <div className="ot-contact">
                                                <span className="ot-contact__avatar" aria-hidden="true">
                                                    {`${(contact.lastName || "").charAt(0)}${(contact.firstName || "").charAt(0)}`.toUpperCase() || <span className="material-symbols-outlined">person</span>}
                                                </span>
                                                <span className="ot-contact__text">
                                                    <span className="ot-contact__name">{`${contact.lastName || ""} ${contact.firstName || ""}`.trim().toUpperCase()}</span>
                                                    <span className="ot-contact__sub">{contact.fiscalCode?.toUpperCase() || ""}</span>
                                                </span>
                                                <span className="ot-contact__count">{group.length}</span>
                                            </div>
                                            <div className="bm-links order-tracking-links">
                                                {group.map((item, itemIndex) => {
                                                    const target = getTargetDetails(item);
                                                    const targetName = target.Target?.[0]?.name || "";
                                                    const carrierKey = targetName.substring(0, 3);
                                                    const carrierFallbackKey = targetName.substring(0, 2);
                                                    const carrierLogo = globals.CARRIER_LOGO[carrierKey] || globals.CARRIER_LOGO[carrierFallbackKey];
                                                    const status = STATUS_ICONS[target.status] || STATUS_ICONS[0];
                                                    const username = `${target.User?.[0]?.username || ""}${target.User_coop?.[0]?.username ? `-${target.User_coop[0].username}` : ""}`;
                                                    return (
                                                        <a
                                                            className="bm-item bm-item--link"
                                                            key={target.id || itemIndex}
                                                            role="button"
                                                            tabIndex={0}
                                                            data-toggle="modal"
                                                            data-target="#modal-order-item"
                                                            onClick={() => setSelectedTargetItem({ ...target })}
                                                            onKeyDown={e => {
                                                                if (e.key === "Enter" || e.key === " ") {
                                                                    e.preventDefault();
                                                                    e.currentTarget.click();
                                                                }
                                                            }}
                                                        >
                                                            <span className={`bm-media${carrierLogo ? " bm-media--logo" : ""}`} aria-hidden="true">
                                                                {carrierLogo
                                                                    ? <img src={`https://x1.dixxip.com/dixcore/public/var/assets${carrierLogo}`} alt="" />
                                                                    : <span className="material-symbols-outlined">receipt_long</span>}
                                                            </span>
                                                            <span className="bm-text">
                                                                <span className="bm-name">{targetName}</span>
                                                                <span className="bm-sub">
                                                                    {target.date ? new Date(target.date).toLocaleDateString("it-IT") : ""}
                                                                    <br />
                                                                    {username ? `${username}` : ""}
                                                                </span>
                                                                <span className="d-flex align-items-center mt-2">
                                                                    {Array.from({ length: 5 }).map((_, index) => (
                                                                        <i
                                                                            key={index}
                                                                            className={index < (Number(target.evaluation) || 0) ? "ik ik-star-on text-yellow" : "ik ik-star-on text-secondary"}
                                                                        />
                                                                    ))}
                                                                </span>
                                                            </span>
                                                            <span className="d-flex flex-column align-items-center" style={{ gap: 4 }}>
                                                                <span className={`material-symbols-outlined bm-arrow ${status.color}`} title={status.label} aria-label={status.label} style={{ opacity: 1 }}>
                                                                    {status.icon}
                                                                </span>
                                                                {Number(target.check) === 1 && (
                                                                    <span className="material-symbols-outlined bm-arrow text-primary" title="Check call" aria-label="Check call" style={{ opacity: 1 }}>
                                                                        counter_3
                                                                    </span>
                                                                )}
                                                            </span>
                                                        </a>
                                                    );
                                                })}
                                            </div>
                                            <hr />
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <OrderItemModal
                companyID={companyID}
                selectedTargetItem={selectedTargetItem}
                users={users}
                userDetails={userDetails}
                onStatusSaved={handleStatusSaved}
                onQualitySaved={handleQualitySaved}
                availableTargets={availableTargets}
            />

            <FooterBar
                footerIcon="timeline"
                footerTitle="Order Tracking"
                footerCount={`${visibleGroups.length} contatti`}
                footerProgress={loadProgress}
                footerButton={
                    <React.Fragment>
                        {userDetails && userDetails.role <= 0 && (
                            <FooterButton icon="edit" title="Crea cliente" action={() => { showInfoToast("Disponibile nel prossimo aggiornamento"); }} />
                        )}
                    </React.Fragment>
                }
                footerPlugin={
                    <React.Fragment>
                        <div className="ot-filter-footer" title="Mostra solo i dati corrispondenti ai filtri">
                            <label className="account-filter-toggle" htmlFor="order-tracking-only-filtered-data">
                                <input
                                    id="order-tracking-only-filtered-data"
                                    className="account-filter-toggle__input"
                                    type="checkbox"
                                    role="switch"
                                    aria-label="Mostra solo i dati corrispondenti ai filtri"
                                    checked={showOnlyFilteredData}
                                    onChange={e => setShowOnlyFilteredData(e.target.checked)}
                                />
                                <span className="account-filter-toggle__track ot-filter-track" aria-hidden="true">
                                    <span className="material-symbols-outlined ot-filter-knob">filter_alt</span>
                                </span>
                            </label>
                        </div>
                        <Calendar rangeStart={dateStart} rangeEnd={dateEnd} setRangeStart={setDateStart} setRangeEnd={setDateEnd} />
                    </React.Fragment>
                }
            />
        </div>
    );
};

export default OrderTracking;